import { i18n } from "../../../boot/i18n";
import { DialogRouter } from "../../../boot/dialogs";
import COLORS from "../../../colors";
import { BottomSheet, Dark, Dialog, Notify, copyToClipboard } from "quasar";
import {
  formatError,
  formatSuccess,
  formatWarning,
  formatHint,
  unescapeHtml,
} from "../../../util/misc";

const resolveNotifyPayload = (payload, valueKey, defaultTimeout) => {
  const hasValue =
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    (valueKey in payload || "timeout" in payload);

  return {
    value: hasValue ? payload[valueKey] : payload,
    timeout:
      hasValue && payload.timeout !== undefined
        ? payload.timeout
        : defaultTimeout,
  };
};

export const PROMPT = (
  context,
  {
    title,
    message,
    prompt,
    options,
    component,
    componentProps,
    ok,
    cancel,
    success,
    failure,
    color = "primary",
    focus = "ok",
  }
) => {
  // A custom component takes over the whole dialog: Quasar ignores
  // title/message/ok/cancel and mounts the component instead. Needed when one
  // dialog has to collect more than the built-in `prompt` allows — that
  // supports a single input and exactly two buttons, and `prompt` and
  // `options` are mutually exclusive.
  let dialog = component
    ? Dialog.create({
        component,
        componentProps,
        class: "non-selectable",
      })
    : Dialog.create({
        title: unescapeHtml(title),
        message: unescapeHtml(message),
        prompt,
        options,
        color,
        // Which button takes focus: "ok", "cancel" or "none". "none" for a
        // prompt a barcode scanner's Enter must not answer.
        focus,
        "no-backdrop-dismiss": true,
        ok: {
          label: ok || i18n.global.t("OK"),
          flat: true,
          color,
        },
        cancel: {
          label: cancel || i18n.global.t("Cancel"),
          flat: true,
          color,
        },
        class: "non-selectable",
      });
  if (success) {
    dialog.onOk(success);
  }
  if (failure) {
    dialog.onCancel(failure);
  }
  return dialog;
};

export const NOTIFY = (context, options) => {
  let fg = Dark.isActive ? "grey-1" : "grey-10";
  let bg = Dark.isActive ? "grey-10" : "grey-1";
  // A named COLORS background already carries the correct pairing — take its
  // fg so light colours like warning/info don't end up with dark-mode white
  // text. An explicit textColor in options still wins via the spread below.
  if (COLORS[options.color]) {
    fg = COLORS[options.color].fg;
    bg = options.color;
  }
  if (options.invert) {
    [bg, fg] = [fg, bg];
  }
  if (options.actions) {
    options.actions.forEach((action) => {
      if (!action.color) {
        action.color = fg;
      }
    });
  }
  return Notify.create({
    progressClass: "bg-" + fg,
    color: bg,
    textColor: fg,
    position: "bottom",
    timeout: 0,
    progress: true,
    actions: [{ icon: "close", color: fg, attrs: { dense: true } }],
    ...options,
  });
};

export const NOTIFY_ERROR = (context, payload) => {
  const { value: error, timeout } = resolveNotifyPayload(payload, "error", 1e4);

  return Notify.create({
    message: formatError(error),
    type: "negative",
    timeout,
    progress: true,
    position: "top-right",
    multiLine: false,
    actions: [{ icon: "close", color: "grey-1", attrs: { dense: true } }],
  });
};

export const NOTIFY_SUCCESS = (context, payload) => {
  const { value: success, timeout } = resolveNotifyPayload(
    payload,
    "success",
    1e4
  );

  return Notify.create({
    message: formatSuccess(success),
    type: "positive",
    timeout,
    progress: true,
    position: "top-right",
    multiLine: false,
    actions: [{ icon: "close", color: "grey-1", attrs: { dense: true } }],
  });
};

export const NOTIFY_WARNING = (context, payload) => {
  const { value: warning, timeout } = resolveNotifyPayload(
    payload,
    "warning",
    0
  );

  return Notify.create({
    message: formatWarning(warning),
    type: "warning",
    timeout,
    progress: true,
    position: "top-right",
    multiLine: false,
    actions: [{ icon: "close", color: "dark", attrs: { dense: true } }],
  });
};

export const NOTIFY_HINT = (context, payload) => {
  const { value: hint, timeout } = resolveNotifyPayload(payload, "hint", 0);

  return Notify.create({
    message: formatHint(hint),
    type: "info",
    timeout,
    progress: true,
    position: "top-right",
    multiLine: false,
    actions: [{ icon: "close", color: "grey-1", attrs: { dense: true } }],
  });
};

export const NOTIFY_TODO = async ({ dispatch }, notification) => {
  const dismiss = () => {
    closed = true;
    closer();
    if (sheet) {
      sheet.hide();
    }
  };
  let sheet = null;
  let closed = false;
  let closer = await dispatch("NOTIFY", {
    message: notification.title + " – " + notification.body,
    group: false,
    invert: true,
    icon: "notification",
    position: "bottom",
    actions: [
      {
        label: i18n.global.t("OK"),
        icon: "done",
        color: "primary",
        handler() {
          dispatch("NOTIFICATION_DONE", { id: notification.id, done: true })
            .then(dismiss)
            .catch((error) => dispatch("NOTIFY_ERROR", error));
        },
      },
      // Make a task of it, when it points into a project. Saving the task
      // marks the notification done; until then it stays, so cancelling the
      // task loses nothing. The bar goes, as the dialog covers the page.
      ...(notification.taskRoute
        ? [
            {
              label: i18n.global.tc("Task"),
              icon: "task",
              color: "primary",
              handler() {
                dismiss();
                DialogRouter.push(notification.taskRoute);
              },
            },
          ]
        : []),
      {
        label: i18n.global.tc("Reminder"),
        icon: "snooze",
        color: "primary",
        handler() {
          sheet = BottomSheet.create({
            title: i18n.global.tc("Reminder") + "…",
            grid: true,
            actions: [
              {
                icon: "snooze",
                label: i18n.global.tc("n_Hours", 1),
                hours: 1,
              },
              {
                icon: "reschedule",
                label: i18n.global.tc("n_Days", 1),
                hours: 24,
              },
              {
                icon: "date_time",
                label: i18n.global.t("Other"),
                hours: 0,
              },
            ],
          })
            .onOk(({ hours }) => {
              sheet = null;
              if (hours) {
                dispatch("NOTIFICATION_SNOOZE", {
                  id: notification.id,
                  hours,
                })
                  .then(dismiss)
                  .catch(async (error) => {
                    closer = await dispatch("NOTIFY_TODO", notification);
                    dispatch("NOTIFY_ERROR", error);
                  });
              } else {
                DialogRouter.push({
                  name: "snooze",
                  params: { id: notification.id },
                });
              }
            })
            .onCancel(async () => {
              sheet = null;
              if (!closed) {
                closer = await dispatch("NOTIFY_TODO", notification);
              }
            });
        },
      },
      { icon: "close", color: "primary", attrs: { dense: true } },
    ],
  });
  return dismiss;
};

export const NOTIFY_UNDO = (
  { dispatch },
  { message, handler, timeout = 5e3, group }
) => {
  return dispatch("NOTIFY", {
    message,
    invert: true,
    group: group || "undo",
    timeout,
    progress: true,
    multiLine: false,
    actions: [
      { label: i18n.global.t("Undo"), color: "primary", handler },
      { icon: "close", color: "primary", attrs: { dense: true } },
    ],
  });
};

export const NOTIFY_COPIED = ({ dispatch }) => {
  return dispatch("NOTIFY", {
    message: formatSuccess("copied"),
    invert: true,
    group: "copied",
    timeout: 1000,
  });
};

export const NOTIFY_ADDRESS = async ({ dispatch }, address) => {
  let closers = [];

  const notify = (field, label) => {
    return dispatch("NOTIFY", {
      message: address[field],
      position: "top-right",
      multiLine: false,
      actions: [
        {
          label,
          icon: "copy",
          handler: () => {
            copyToClipboard(address[field]);
          },
        },
        { icon: "close", attrs: { dense: true } },
      ],
    });
  };

  if (address.zip) {
    closers.push(await notify("zip", i18n.global.t("Zip")));
  }
  if (address.city) {
    closers.push(await notify("city", i18n.global.t("City")));
  }
  if (address.street) {
    closers.push(await notify("street", i18n.global.t("Street")));
  }

  return closers;
};
